import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye, FaFileInvoiceDollar, FaTimes } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Grid from "../components/Grid"; // Import the Grid component
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function OrderRequest({ onInvoicingClick, onAddOrderRequestClick }) {
  const [orderRequests, setOrderRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSearchField, setSelectedSearchField] =
    useState("orderRequestCode");
  const [selectedOrderRequest, setSelectedOrderRequest] = useState(null);
  const [userRole, setUserRole] = useState(""); // State to store user role
  const [locations, setLocations] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [organizations, setOrganizations] = useState([]); // List of organizations for Main Admin
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [selectedOrderRequestIdToCancel, setSelectedOrderRequestIdToCancel] = useState(null);

  useEffect(() => {

    // Fetch the logged-in user's role and organization data
    const fetchUserRoleAndOrganizations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        // Fetch user role
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUserRole(userResponse.data.role);

        // Fetch organizations if the user is a Main Admin
        if (userResponse.data.role === "Main Admin" || userResponse.data.role === "Farm User" || userResponse.data.role === "Outlet User") {
          const orgResponse = await axios.get(`${API_BASE_URL}/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setOrganizations(orgResponse.data.organizations || []);
        }
      } catch (error) {
        console.error("Error fetching user role or organizations:", error);
        toast.error("Error fetching data");
      }
    };

    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
          if (!token) {
              toast.error("Authentication token missing. Please log in.");
              return;
          }
          try {
              const response = await axios.get(
                  `${API_BASE_URL}/users/specific`,
                  {
                  headers: { Authorization: `Bearer ${token}` },
                  }
          );
          const userData = response.data;
          setCurrentUser(userData);
          // Fetch locations and products based on role
          if (userData.role === "Main Admin") {
              fetchAllLocations();
          } else if (userData.role === "Company Admin" || userData.role === "Farm User" || userData.role === "Outlet User") {
              fetchFilteredLocations(userData);
          }
          } catch (error) {
              console.error("Error fetching user data:", error);
              toast.error("Failed to fetch user data.");
          }
  
          fetchCurrentUser();
    };

    const fetchAllLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
          `${API_BASE_URL}/locations`,
          {
              headers: { Authorization: `Bearer ${token}` },
          }
      );
      setLocations(response.data.locations);
    };

    const fetchFilteredLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
          `${API_BASE_URL}/products/filtered-locations`,
          {
          headers: { Authorization: `Bearer ${token}` },
          }
      );
      setLocations(response.data.locations);
    };

    const fetchOrderRequests = async () => {
      try { 
        const token = localStorage.getItem("token"); // Retrieve token from localStorage
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
        const response = await axios.get(
          `${API_BASE_URL}/orders/order-requests/get-all-order-requests`,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Include token in Authorization header
            },
          }
        );
        const formattedRequests = response.data.orderRequests.map((order) => ({
          ...order,
          id: order.orderRequestCode, // Use orderRequestCode as ID
          customer_id: order.customer_id || "Unknown", // Include customer_id if available
        }));
        setOrderRequests(formattedRequests);

        
      } catch (error) {
        console.error("Error fetching Order Requests:", error);
        toast.error("Error fetching Order Requests");
      }
    };

    fetchOrderRequests();
    fetchUserRoleAndOrganizations();
  }, [userRole]);


  const handleOrderRequestClick = (order) => {
    setSelectedOrderRequest(order);
  };

  const handleDownloadExcel = async () => {
  const dataToExport = searchTerm.trim().length > 0 ? filteredOrderRequests : orderRequests;

    if (dataToExport.length === 0) {
      toast.warn("No order request data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Order Requests");

    const headerRow = [
      "Order Request ID",
      "Customer ID",
      "Customer Name",
      "Location Name",
      "Pickup Location Name",
      "Total Price (LKR)",
      "Status",
      "Created Date",
      "Created By",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    const formatDate = (dateStr) => {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
    };

    dataToExport.forEach((order) => {
      worksheet.addRow([
        order.orderRequestCode || "N/A",
        order.customer_id || "N/A",
        order.customer_name || "N/A",
        order.location_name || "N/A",
        order.pickup_location_name || "N/A",
        Number(order.totalOrderPrice || 0).toFixed(2),
        order.status || "N/A",
        formatDate(order.createdAt) || "N/A",
        order.types || "N/A", 
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 25
          ? 25
          : column.header.toString().length
        : 25;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "order_requests.xlsx");
  };

  const handleClosePopup = () => {
    setSelectedOrderRequest(null);
  };

  const handleInvoicing = (order) => {

    if(order.status === "Cancelled") {
      toast.error("Cannot create sales order for cancelled order request.");
      return;
    }


    // Pass the data to the parent's content change function
    onInvoicingClick({
      orderRequestCode: order.orderRequestCode,
      orderRequest: order,
    });
  };

  const cancelOrderRequest = async(orderRequestId) => {
    
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            toast.error("Authentication token is missing. Please log in again.");
            return;
          }
    
          const response = await axios.post(`${API_BASE_URL}/orders/cancel`, {orderRequestId}, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
    
          toast.success("OrderRequest cancelled successfully!");
        }
        catch (error) {
          if (error.response && error.response.status === 400) {
            toast.error(error.response.data.message || "Failed to cancel Invoice.");
          } else {
            toast.error("Failed to cancel invoice. Please try again.");
          }
        }
      }
  
    const handleCancelClick = (id) => {
      setSelectedOrderRequestIdToCancel(id);
      setConfirmCancelOpen(true);
    };
  
    const confirmCancel = () => {
      cancelOrderRequest(selectedOrderRequestIdToCancel);
      setConfirmCancelOpen(false);
      setSelectedOrderRequestIdToCancel(null);
    };
  
    const cancelDialog = () => {
      setConfirmCancelOpen(false);
      setSelectedOrderRequestIdToCancel(null);
    };

  const filteredOrderRequests = orderRequests
  .filter((order) => {
    switch (selectedSearchField) {
      case "customer_name":
        return order.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
      case "location_name":
        return order.location_name.toLowerCase().includes(searchTerm.toLowerCase());
      case "pickup_location_name":
        return order.pickup_location_name.toLowerCase().includes(searchTerm.toLowerCase());
      case "status":
        return order.status.toLowerCase().includes(searchTerm.toLowerCase());
      case "orderRequestCode":
        return order.orderRequestCode.toLowerCase().includes(searchTerm.toLowerCase());
      case "customer_id":
        return order.customer_id.toLowerCase().includes(searchTerm.toLowerCase());
      case "totalOrderPrice":
        return order.totalOrderPrice.toString().includes(searchTerm);
      case "createdDate":
        return (
          order.createdAt &&  
          new Date(order.createdAt).toLocaleDateString("en-GB").includes(searchTerm)
        );
      case "types":
        return (
          order.types &&
          order.types.toLowerCase().includes(searchTerm.toLowerCase()
          )
        );
      default:
        return true; // Return all orders if no filter is applied
    }
  })
  .sort((a, b) => {
    // Ensure the latest created orders are displayed first
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA; // Newest first
  });

  const columns = [
    { field: "orderRequestCode", headerName: "Order Request ID", flex: 1 },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 1,
      renderCell: (params) => (
        <span>
          {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
        </span>
      ),
},
    { field: "customer_name", headerName: "Customer Name", flex: 1 },
    { field: "customer_id", headerName: "Customer ID", flex: 1 }, // New column for Customer ID
    { field: "location_name", headerName: "Location Name", flex: 1 },
    { field: "pickup_location_name", headerName: "Pickup Location Name", flex: 1 },
    {
      field: "totalOrderPrice",
      headerName: "Total Price (LKR)",
      flex: 1,
      headerAlign: "right",
      renderCell: (params) => (
        <div style={{ textAlign: "right", width: "100%", paddingRight: "15px" }}>
          {new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(params.value)}
        </div>
      ),
    },    
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span
          className={`px-2 py-1 rounded-sm text-white ${
            params.row.status === "Pending"
              ? "bg-[#FBC02D]"
              : params.row.status === "In-Progress"
              ? "bg-[#8BC34A]"
              : params.row.status === "Completed"
              ? "bg-[#90A4AE]"
              : params.row.status === "Closed"
              ? "bg-[#E57373]"
              : "bg-[#E57373]"
          }`}
        >
          {params.row.status}
        </span>
      ),
    },
      {
        field: "types",
        headerName: "Created By",
        flex: 1,
      },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex p-3 space-x-3 justify-center items-center">
        {/*Cancel Button*/}
          <button
              onClick={() => handleCancelClick(params.row.id)}
              className="bg-[#d82828] text-white p-2 flex items-center justify-center"
              >
              <FaTimes className="text-white" />
          </button>
          {/* Existing View Button */}
          <button
            onClick={() => handleOrderRequestClick(params.row)}
            className="bg-[#FBC02D] text-white p-2 flex items-center justify-center"
          >
            <FaEye className="text-white" />
          </button>

          {/* Invoicing Button */}
          
            <button
              onClick={() => handleInvoicing(params.row)}
              className="bg-[#4CAF50] text-white p-2 flex items-center justify-center"
            >
              <FaFileInvoiceDollar className="text-white" />
            </button>
         
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col p-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-green-700">Order Requests</h1>

        {(userRole === "Farm User" || userRole === "Outlet User" || userRole === "Company Admin") && (
          <button 
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
            onClick={onAddOrderRequestClick}
          >
            Add Order Request
          </button>
        )}
      </div>
      <div className="bg-white p-4 rounded-lg shadow mb-8">
      <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="customer_name">Customer Name</option>
                <option value="location_name">Location Name</option>
                <option value="status">Status</option>
                <option value="orderRequestCode">Order Request ID</option>
                <option value="pickup_location_name">Pickup Location Name</option>
                <option value="customer_id">Customer ID</option>
                <option value="totalOrderPrice">Total Price</option>
                <option value="createdDate">Created Date</option>
                <option value="types">Created By</option>
                
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Payments"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>        
        <div style={{ width: "100%" }}>
          <Grid
            rows={filteredOrderRequests.map((order) => ({
              ...order,
              id: order.orderRequestCode, // Ensure ID is unique
            }))}
            columns={columns}
            pageSize={5}
            getRowId={(row) => row.orderRequestCode}
          />
        </div>
      </div>

      {selectedOrderRequest && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
          style={{ overflowY: "auto" }}
        >
          <div
            className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <button
              onClick={handleClosePopup}
              className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
            >
              &times;
            </button>
            <h2 className="text-xl font-semibold mb-4">
              Order Request Details : {selectedOrderRequest.orderRequestCode}
            </h2>
            <div>
              <p>
                <strong className="text-sm font-semibold">
                  Customer Name:
                </strong>{" "}
                {selectedOrderRequest.customer_name}
              </p>
              <p>
                <strong className="text-sm font-semibold">
                  Customer ID:
                </strong>{" "}
                {selectedOrderRequest.customer_id}
              </p>
              <p>
                <strong className="text-sm font-semibold">
                  Location Name:
                </strong>{" "}
                {selectedOrderRequest.location_name}
              </p>
              <p>
                <strong className="text-sm font-semibold">
                  Pickup Location Name:
                </strong>{" "}
                {selectedOrderRequest.pickup_location_name}
              </p>
              <p>
                <strong className="text-sm font-semibold">Total Price:</strong>{" "}
                LKR{" "}
                {parseFloat(selectedOrderRequest.totalOrderPrice).toFixed(2)}
              </p>
              <p>
                <strong className="text-sm font-semibold">Status:</strong>{" "}
                {selectedOrderRequest.status}
              </p>
              <p>
                <strong className="text-sm font-semibold">
                  Description:
                </strong>{" "}
                {selectedOrderRequest.description || "No description provided"}
              </p>
            </div>
            <h3 className="text-lg font-semibold mt-4">Order Items</h3>
            <div style={{ width: "100%" }}>
              <DataGrid
                autoHeight
                rows={selectedOrderRequest.orderItems.map((item, index) => ({
                  id:
                    item.id ||
                    `${selectedOrderRequest.orderRequestCode}-${index}`,
                  product_code: item.product_code,
                  product_name: item.product_name,
                  quantity: item.quantity,
                  unit: item.unit,
                  unit_price: parseFloat(item.unit_price).toFixed(2),
                  total_price: parseFloat(item.total_price).toFixed(2),
                }))}
                columns={[
                  {
                    field: "product_code",
                    headerName: "Product Code",
                    width: 150,
                    flex: 0.7,
                  },
                  {
                    field: "product_name",
                    headerName: "Product Name",
                    width: 200,
                    flex: 1,
                  },
                  { field: "quantity", headerName: "Quantity", width: 100, flex: 1, headerAlign: "right", align: "right"},
                  { 
                    field: "unit", 
                    headerName: "Units", 
                    flex: 1, 
                    headerAlign: "center", 
                    align: "center" 
                  },
                  {
                    field: "unit_price",
                    headerName: "Unit Price (LKR)",
                    width: 150,
                    headerAlign: "right",
                    align: 'right',
                    renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}`,
                    flex: 1,
                  },
                  {
                    field: "total_price",
                    headerName: "Total Price (LKR)",
                    headerAlign: "right",
                    align: "right",
                    width: 150,
                    renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}`,
                    flex: 1,
                  },
                ]}
                pageSize={5}
              />
            </div>
          </div>
        </div>
      )}
      {confirmCancelOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-md w-80">
          <h2 className="text-lg font-semibold mb-4">Confirm Cancellation</h2>
          <p className="mb-6">Are you sure you want to cancel this Order Request?</p>
          <div className="flex justify-end space-x-4">
            <button onClick={cancelDialog} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
            <button onClick={confirmCancel} className="bg-red-600 text-white px-4 py-2 rounded">Confirm</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

export default OrderRequest;